import React, { useState, useRef, useEffect, memo, useMemo } from 'react';
import { Calendar, Clock, Building, Briefcase, Globe, Loader2, Settings } from 'lucide-react';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import configService, { Language } from '../../services/configService';
import { toast } from 'react-hot-toast';
import { useToast } from '../common/Toast';
import { useNavigate } from 'react-router-dom';
import { DurationDisplay } from '../common/DurationDisplay';

interface InterviewDetails {
  id?: string;
  title?: string;
  company?: string;
  jobRole?: string;
  duration?: number;
  description?: string;
  language?: string;
}

interface ScheduleInterviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSchedule: (date: Date, languageId?: string) => Promise<void>;
  interviewDetails?: InterviewDetails;
  initialDate?: string;
  mode?: 'schedule' | 'reschedule';
}

export const ScheduleInterviewModal: React.FC<ScheduleInterviewModalProps> = memo(({
  isOpen,
  onClose,
  onSchedule,
  interviewDetails = {},
  initialDate = new Date().toISOString(), // Default to now
  mode = 'schedule'
}) => {
  // Track loading state for the submit button
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();
  // Track if this is the initial render
  const isInitialRender = useRef(true);
  // Track if user has manually modified the date/time
  const [userHasModifiedDate, setUserHasModifiedDate] = useState(false);
  const [userHasModifiedTime, setUserHasModifiedTime] = useState(false);
  
  // Store the initial date value to prevent changing on re-renders
  const initialDateRef = useRef(initialDate);
  
  console.log("Rendering ScheduleInterviewModal with initialDate:", initialDate);
  console.log("User modifications - Date:", userHasModifiedDate, "Time:", userHasModifiedTime);

  // Initialize state with the initial date (or current date if not provided)
  const [selectedInterviewDate, setSelectedInterviewDate] = useState<Date>(() => {
    const date = new Date(initialDateRef.current);
    console.log("Initial selectedDate:", date.toString());
    return date;
  });

  // Initialize time separately from the date
  const [selectedTime, setSelectedTime] = useState<string>(() => {
    const date = new Date(initialDateRef.current);
    const timeString = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    console.log("Initial selectedTime:", timeString);
    return timeString;
  });

  const [selectedLanguage, setSelectedLanguage] = useState<string>(interviewDetails.language || 'English');
  const [selectedLanguageId, setSelectedLanguageId] = useState<string>('');
  const [languages, setLanguages] = useState<Language[]>([]);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState<boolean>(false);
  const navigate = useNavigate();
  
  // Refactored formattedDate to use local date instead of UTC
  // This ensures the displayed date matches the user's selected date regardless of timezone
  const formattedDate = useMemo(() => {
    // Format date in YYYY-MM-DD format using local date components
    const year = selectedInterviewDate.getFullYear();
    const month = String(selectedInterviewDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedInterviewDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, [selectedInterviewDate]);
  
  // Log current values on every render for debugging
  console.log("Current values on render:", {
    selectedDate: selectedInterviewDate.toString(),
    selectedTime,
    formattedDate,
    dateObject: {
      year: selectedInterviewDate.getFullYear(),
      month: selectedInterviewDate.getMonth() + 1,
      day: selectedInterviewDate.getDate()
    }
  });
  
  // Load languages on component mount
  useEffect(() => {
    const loadLanguages = async () => {
      setIsLoadingLanguages(true);
      try {
        const languagesData = await configService.getLanguages();
        setLanguages(languagesData);
        
        // Find the language ID for the selected language name
        const language = languagesData.find(lang => lang.name === selectedLanguage);
        if (language) {
          setSelectedLanguageId(language._id);
        } else if (languagesData.length > 0) {
          // Set default language if current selection is not found
          setSelectedLanguage(languagesData[0].name);
          setSelectedLanguageId(languagesData[0]._id);
        }
      } catch (error) {
        console.error('Error loading languages:', error);
      } finally {
        setIsLoadingLanguages(false);
      }
    };
    
    loadLanguages();
  }, [selectedLanguage]);
  
  // Only update state from initialDate on the first render or if user hasn't modified values
  useEffect(() => {
    // Skip if this isn't the initial render and initialDate hasn't changed from initial value
    if (!isInitialRender.current && initialDate === initialDateRef.current) {
      console.log("Skipping initialDate effect - same value");
      return;
    }
    
    // Skip if user has manually modified date or time
    if (userHasModifiedDate || userHasModifiedTime) {
      console.log("Skipping initialDate effect - user has made manual modifications");
      return;
    }
    
    console.log("initialDate effect running:", initialDate);
    
    // Convert the initialDate (which is ISO string) to a Date object in the user's local timezone
    const date = new Date(initialDate);
    
    // When setting the selectedInterviewDate, we need to ensure the date part is preserved
    // regardless of the time and timezone conversions
    setSelectedInterviewDate(date);
    
    const newTimeString = `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    console.log("Setting new time from initialDate:", newTimeString);
    setSelectedTime(newTimeString);
    
    // No longer the initial render
    isInitialRender.current = false;
  }, [initialDate, userHasModifiedDate, userHasModifiedTime]);
  
  // References to input elements
  const dateInputRef = useRef<HTMLInputElement>(null);
  const timeInputRef = useRef<HTMLInputElement>(null);

  // Handle date changes
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateValue = e.target.value;
    console.log("Date input changed to:", dateValue);
    
    // Mark that user has manually changed the date
    setUserHasModifiedDate(true);
    
    if (dateValue) {
      // Parse the date using local date components to avoid timezone issues
      const [year, month, day] = dateValue.split('-').map(Number);
      
      // Create a new date preserving the time from the current selected date
      const hours = selectedInterviewDate.getHours();
      const minutes = selectedInterviewDate.getMinutes();
      
      // Create date using local components to ensure timezone consistency
      const newDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
      
      if (!isNaN(newDate.getTime())) {
        console.log("Setting new date:", newDate.toString());
        console.log("Date components:", { year, month, day, hours, minutes });
        setSelectedInterviewDate(newDate);
      }
    }
  };

  // Add this function before the component's return statement
  const getMinTime = () => {
    const today = new Date();
    const todayFormatted = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    // Check if selected date is today by comparing formatted dates
    if (formattedDate === todayFormatted) {
      // If today, min time is current time
      const hours = today.getHours().toString().padStart(2, '0');
      const minutes = today.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    
    // If future date, no min time restriction
    return '';
  };

  // Modify the handleTimeChange function to validate time selection
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    
    // Check if selected date is today
    const today = new Date();
    const selectedDate = new Date(selectedInterviewDate);
    
    if (selectedDate.toDateString() === today.toDateString()) {
      // Parse times for comparison
      const [inputHours, inputMinutes] = newTime.split(':').map(Number);
      const currentHours = today.getHours();
      const currentMinutes = today.getMinutes();
      
      // Check if selected time is in the past
      if (inputHours < currentHours || (inputHours === currentHours && inputMinutes < currentMinutes)) {
        console.warn("handleTimeChange: Selected time is in the past");
        toast.error("Please select a time in the future");
        // Reset to current time
        const hours = currentHours.toString().padStart(2, '0');
        const minutes = currentMinutes.toString().padStart(2, '0');
        setSelectedTime(`${hours}:${minutes}`);
        return;
      }
    }
    
    // Mark that user has manually changed the time
    setUserHasModifiedTime(true);
    
    setSelectedTime(newTime);
    
    // Update the date object with the new time
    if (newTime) {
      // Extract hours and minutes from the time string
      const [hours, minutes] = newTime.split(':').map(Number);
      
      // Create a new date object to avoid mutating the original
      const newDate = new Date(selectedInterviewDate);
      newDate.setHours(hours, minutes, 0, 0);
      
      if (!isNaN(newDate.getTime())) {
        console.log("Setting new date with updated time:", newDate.toString());
        setSelectedInterviewDate(newDate);
      }
    }
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const languageName = e.target.value;
    setSelectedLanguage(languageName);
    
    // Update the language ID based on the selected name
    const language = languages.find(lang => lang.name === languageName);
    if (language) {
      setSelectedLanguageId(language._id);
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Set loading state
    setIsSubmitting(true);
    
    try {
      // Parse the selected time string into hours and minutes
      const [hours, minutes] = selectedTime.split(':').map(Number);
      
      // Get the date components from the actual formatted date to ensure correct date
      const [year, month, day] = formattedDate.split('-').map(Number);
      
      // Create the final date with the exact components the user selected
      const finalDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
      
      console.log("User selected date (local):", formattedDate);
      console.log("User selected time:", selectedTime);
      console.log("Submitting scheduled date (local):", finalDate.toString());
      console.log("UTC date for server:", finalDate.toISOString());
      console.log("Selected language ID:", selectedLanguageId);
      
      // Check if the time is in the past
      if (finalDate < new Date()) {
        console.warn("handleSubmit: Selected time is in the past");
       showToast("Please select a time in the future", "error");
        return;
      }
      
      // Wait for the onSchedule Promise to resolve
      await onSchedule(finalDate, selectedLanguageId);
      
      // Only close the modal if the API call was successful
      onClose();
    } catch (error) {
      console.error("Error scheduling interview:", error);
      // Don't close the modal on error
    } finally {
      // Reset loading state
      setIsSubmitting(false);
    }
  };
  
  const modalTitle = mode === 'reschedule' ? 'Reschedule Interview' : 'Schedule New Interview';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={modalTitle} maxWidth="550px">
      <div className="p-6">
        {/* Interview Details (if available) */}
        {(interviewDetails.title || interviewDetails.company || interviewDetails.jobRole) && (
          <div className="mb-6 p-4 rounded-lg border border-border-light dark:border-border-dark bg-background-light/70 dark:bg-background-dark/70 focus-within:ring-2 focus-within:ring-primary-light dark:focus-within:ring-primary-dark focus-within:border-transparent transition-all">
            {interviewDetails.title && (
              <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark mb-2">
                {interviewDetails.title}
              </h3>
            )}
            
            {interviewDetails.description && (
              <div className="mb-3 text-sm text-text-secondary-light dark:text-text-secondary-dark">
                {interviewDetails.description}
              </div>
            )}

            <div className="flex flex-col gap-2">
              {interviewDetails.company && (
                <div className="flex items-center text-sm text-text-secondary-light dark:text-text-secondary-dark">
                  <Building size={16} className="mr-2 text-primary-light dark:text-primary-dark" />
                  {interviewDetails.company}
                </div>
              )}
              
            
              {interviewDetails.jobRole && (
                <div className="flex items-center text-sm text-text-secondary-light dark:text-text-secondary-dark">
                  <Briefcase size={16} className="mr-2 text-primary-light dark:text-primary-dark" />
                  {interviewDetails.jobRole}
                </div>
              )}
              
              {/* Duration display hidden as it can be confusing for users
              {interviewDetails.duration && (
                <DurationDisplay 
                  minutes={interviewDetails.duration}
                  variant="inline"
                  clockSize={16}
                  className="text-sm text-text-secondary-light dark:text-text-secondary-dark"
                />
              )} */}
              
              {interviewDetails.language && (
                <div className="flex items-center text-sm text-text-secondary-light dark:text-text-secondary-dark">
                  <Globe size={16} className="mr-2 text-primary-light dark:text-primary-dark" />
                  {interviewDetails.language}
                </div>
              )}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Date Picker */}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Calendar size={18} className="text-primary-light dark:text-primary-dark -mt-2" />
                <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark">
                  Select Date
                </label>
              </div>
              <div className="text-start bg-background-light/70 dark:bg-background-dark/70 border border-border-light dark:border-border-dark rounded-lg p-0">
                <input 
                  id="date-input"
                  ref={dateInputRef}
                  type="date" 
                  value={formattedDate}
                  min={new Date().toISOString().split('T')[0]} // Set min to today's date so that user can't select past dates
                  onChange={handleDateChange}
                  className="text-start bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm cursor-pointer"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Time Picker */}
            <div>
              <div className="flex flex-row items-center gap-2 mb-1">
                <Clock size={18} className="text-primary-light dark:text-primary-dark -mt-2" />
                <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark">
                  Select Time
                </label>
              </div>
              <div className="text-start bg-background-light/70 dark:bg-background-dark/70 border border-border-light dark:border-border-dark rounded-lg p-0">
                <input 
                  id="time-input"
                  ref={timeInputRef}
                  type="time" 
                  min={getMinTime()} // Dynamic min time based on selected date
                  value={selectedTime}
                  onChange={handleTimeChange}
                  className="text-start bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm cursor-pointer"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            
            {/* Language Selection */}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Globe size={18} className="text-primary-light dark:text-primary-dark -mt-2" />
                <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark">
                  Select Language
                </label>
              </div>
              <div className="bg-background-light/70 dark:bg-background-dark/70 border border-border-light dark:border-border-dark rounded-lg p-0">
                <select
                  value={selectedLanguage}
                  onChange={handleLanguageChange}
                  className="bg-transparent border-none w-full focus:outline-none text-text-primary-light dark:text-text-primary-dark text-sm cursor-pointer"
                  disabled={isLoadingLanguages || isSubmitting}
                >
                  {isLoadingLanguages ? (
                    <option>Loading languages...</option>
                  ) : languages.length > 0 ? (
                    languages.map(language => (
                      <option key={language._id} value={language.name}>
                        {language.name}
                      </option>
                    ))
                  ) : (
                    // Fallback options if API fails
                    <>
                      <option>No Language Selected</option>
                    </>
                  )}
                </select>
              </div>
            </div>
            
            {/* Duration - only show if not already in interview details */}
            {!interviewDetails.duration && (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Clock size={18} className="text-primary-light dark:text-primary-dark -mt-2" />
                  <label className="text-base font-heading font-semibold text-text-primary-light dark:text-text-primary-dark">
                    Duration
                  </label>
                </div>
                <div className="relative">
                  <select
                    defaultValue="60"
                    className="w-full px-3 py-2 text-sm rounded-lg bg-background-light/70 dark:bg-background-dark/70 border border-border-light dark:border-border-dark focus:outline-none focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark appearance-none pr-10"
                    disabled={isSubmitting}
                  >
                    <option value="30">30 minutes</option>
                    <option value="45">45 minutes</option>
                    <option value="60">60 minutes</option>
                    <option value="90">90 minutes</option>
                    <option value="120">120 minutes</option>
                  </select>
                </div>
              </div>
            )}
            <div className="bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark p-4 mb-4">
              <div className="flex flex-col justify-between space-y-4">
                <div className="flex flex-col space-y-2">
                  <div className="flex items-center">
                    <img src="/google-calendar.png" alt="Google Calendar" className="w-5 h-5 mr-2" />
                    <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">Calendar Reminders</h3>
                  </div>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    Connect your Google Calendar to Mocktrail to receive interview reminders and enhance your interview preparation experience.
                  </p>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  type="button"
                  className="py-3 px-4 w-[40%]"
                  onClick={() => {
                    window.open('/settings-preferences', '_blank');
                  }}
                  rightIcon={<Settings size={16} />}
                >
                  <span className="text-sm">Open Settings</span>
                </Button>
              </div>
            </div>
            {/* Action Buttons */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 mt-8">
              <Button
                variant="outline"
                onClick={onClose}
                type="button"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                leftIcon={isSubmitting ? <Loader2 size={16} className="animate-spin text-white" /> : <Calendar size={16} className="text-white" />}
                disabled={isSubmitting}
              >
                {isSubmitting 
                  ? (mode === 'reschedule' ? 'Rescheduling...' : 'Scheduling...') 
                  : (mode === 'reschedule' ? 'Reschedule' : 'Schedule') + ' Interview'
                }
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
});

ScheduleInterviewModal.displayName = 'ScheduleInterviewModal';

export default ScheduleInterviewModal; 